import CheckIcon from '@mui/icons-material/Check'
import { LoadingButton } from '@mui/lab'
import { Button, Card, CardActions, CardContent, CardHeader, Divider, Typography } from '@mui/material'
import { Box, useMediaQuery, useTheme } from '@mui/system'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import DOMPurify from 'dompurify'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNotification } from '../../../store/redux/hooks'
import { getCurrentUser, getIsAdmin, getIsManagement } from '../../../store/redux/reducers/currentUserSlice'
import { useMarkPushNotificationReadMutation } from '../../../store/redux/reducers/pushNotificationSlice'
import { useGetVacationAcknowledgementQuery } from '../../../store/redux/reducers/vacationAcknowledgementApiSlice'
import { User } from '../../../store/types'
import { NotificationReason } from '../../../store/types/enums/NotificationReason'
import PushNotification from '../../../store/types/models/PushNotification'
import { Vacation } from '../../../store/types/utils/VacationTypes'
import AvatarComponent from '../../avatar/AvatarComponent'
import SystemNotificationCardLogo from './SystemNotificationCardLogo'

dayjs.extend(utc)
dayjs.extend(timezone)

type NotificationCardProps = {
  notification: PushNotification
  toggleAcceptVacationButton: (vacation: Vacation, notificationId: string) => void
  setAcceptedVacation: React.Dispatch<React.SetStateAction<Vacation | null>>
}

const SYSTEM_NOTIFICATIONS = [NotificationReason.MONTH_SUMMARY, NotificationReason.EMPLOYEE_UPCOMING_VACATION]

const NotificationCard = ({ notification, toggleAcceptVacationButton, setAcceptedVacation }: NotificationCardProps) => {
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const currentUser = useSelector(getCurrentUser)
  const isAdmin = useSelector(getIsAdmin)
  const isManagement = useSelector(getIsManagement)
  const { displayNotification } = useNotification()

  const canAcceptVacation =
    isAdmin || isManagement || notification.employee?.leaders?.some(leader => leader.id === currentUser?.id)

  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false)
  const [avatar, setAvatar] = useState<User>()
  const [isVacationNotification, setIsVacationNotification] = useState<boolean>(false)
  const [isRead, setIsRead] = useState<boolean>(notification.isRead)
  const [markRead, { isLoading }] = useMarkPushNotificationReadMutation()

  const { data: ackData } = useGetVacationAcknowledgementQuery(notification.vacation?.id?.toString()!, {
    skip: !notification.vacation,
  })

  useEffect(() => {
    setIsAcknowledged(ackData?.acknowledgements?.some(ack => ack.acknowledgedBy === currentUser?.id) ?? false)
  }, [ackData, currentUser])

  useEffect(() => {
    setIsRead(notification.isRead)
  }, [notification.isRead])

  useEffect(() => {
    if (
      notification.reason === NotificationReason.ADMINISTRATION_NOTIFICATION ||
      notification.reason === NotificationReason.WORK_HOURS_NOT_FILLED
    ) {
      setAvatar(notification.createdBy)
    } else if (
      notification.reason === NotificationReason.MONTH_SUMMARY ||
      notification.reason === NotificationReason.EMPLOYEE_UPCOMING_VACATION
    ) {
      setAvatar(undefined)
    } else {
      setAvatar(notification.employee)
    }

    setIsVacationNotification(
      notification.reason === NotificationReason.EMPLOYEE_UPCOMING_VACATION ||
        notification.reason === NotificationReason.EMPLOYEE_VACATION_CHANGED ||
        notification.reason === NotificationReason.EMPLOYEE_VACATION_CREATED
    )
  }, [notification])

  const handleMarkRead = async () => {
    if (isRead) return

    try {
      await markRead(notification.id).unwrap()
      setIsRead(true)
    } catch (err: any) {
      setIsRead(false)

      displayNotification({
        open: false,
        type: 'error',
        message: t('components.notificationSidebar.markSeenErrorMessage'),
        timeout: 5000,
      })
    }
  }

  const handleAcceptVacation = () => {
    setAcceptedVacation(notification.vacation)
    toggleAcceptVacationButton(notification.vacation, notification.id)
  }

  return (
    <Card elevation={4} sx={{ flexShrink: 0 }}>
      <CardHeader
        sx={{ pt: 1 }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {SYSTEM_NOTIFICATIONS.includes(notification.reason) ? (
                <SystemNotificationCardLogo />
              ) : (
                <>
                  {avatar && (
                    <Box sx={{ pr: 0.5 }}>
                      <AvatarComponent
                        avatar={avatar.avatar}
                        extension={avatar.extension}
                        alt={`${avatar.firstName} ${avatar.lastName}`}
                        size={32}
                      />
                    </Box>
                  )}
                </>
              )}

              <Box>
                <Typography component={'h2'} variant={'h6'} lineHeight={1.43}>
                  {SYSTEM_NOTIFICATIONS.includes(notification.reason)
                    ? t('components.notificationSidebar.systemNotification')
                    : avatar && `${avatar.firstName} ${avatar.lastName}`}
                </Typography>
                <Typography variant={'body2'}>
                  {dayjs.utc(notification.createdAt).local().format('DD.MM.YYYY HH:mm')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant={'body2'}
                sx={{
                  fontWeight: 700,
                  color: isRead ? '#2e7d32' : '#d32f2f',
                  whiteSpace: 'nowrap',
                }}
              >
                • {isRead ? t('components.notificationSidebar.seen') : t('components.notificationSidebar.unseen')}
              </Typography>
            </Box>
          </Box>
        }
      />
      <CardContent
        sx={{ wordBreak: 'break-word', pt: 0 }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notification.message) }}
      />
      {(!isRead || (isVacationNotification && notification.vacation !== null && !isAcknowledged)) && (
        <>
          <Divider />
          <CardActions
            sx={{
              justifyContent: 'end',
              alignItems: 'end',
              flexDirection: isDownSm ? 'column' : 'row',
              gap: isDownSm ? 0.5 : 0,
            }}
          >
            {isVacationNotification && !isAcknowledged && ackData && canAcceptVacation && (
              <Button onClick={handleAcceptVacation} size="small" color="primary">
                {!isDownSm && <CheckIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />}
                {t('components.notificationSidebar.acceptVacation')}
              </Button>
            )}
            <LoadingButton
              onClick={handleMarkRead}
              disabled={isLoading || isRead}
              size="small"
              color="inherit"
              sx={{ visibility: isRead ? 'hidden' : '' }}
            >
              {!isDownSm && <CheckIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />}
              {t('components.notificationSidebar.markSeen')}
            </LoadingButton>
          </CardActions>
        </>
      )}
    </Card>
  )
}

export default NotificationCard
